import Col from 'antd/lib/col';
import DatePicker from 'antd/lib/date-picker';
import Input from 'antd/lib/input';
import InputNumber from 'antd/lib/input-number';
import Typography from 'antd/lib/typography';
import dayjs from 'dayjs';
import { defaultDateFormat } from 'src/domain/common/dateFormatters';
import { BillFieldErrors, ICustomerUtilityBill, MAX_BILLING_VALUE, TUpdatableBillField } from 'src/domain/customer-utility-bill/interface';
import { EMPTY_VALUE, billLabelStyle, billValueStyle } from '../billStyles';

const { Text } = Typography;

const PERIOD_VALUE_FORMAT = 'YYYY-MM-DD';
const PERIOD_DISPLAY_FORMAT = 'MMM D, YYYY';
// Same display as PERIOD_DISPLAY_FORMAT, in date-fns tokens for the read-only value.
const PERIOD_READ_ONLY_FORMAT = 'MMM d, yyyy';

export type TBillFieldChange = <K extends keyof ICustomerUtilityBill>(fieldName: K, value: ICustomerUtilityBill[K]) => void;

// What every editable section receives from EditBillModal.
export interface IBillSectionProps {
    form: ICustomerUtilityBill;
    // Fields the operator may edit for this bill (derived from its source-file row).
    editableFields: Set<TUpdatableBillField>;
    errors: BillFieldErrors;
    // Fields needed for Parsing Success that are currently empty — flagged with a (non-blocking) warning.
    missingFields: Set<TUpdatableBillField>;
    // View-only bills (not yet parsed / download-failed) open read-only.
    disabled?: boolean;
    handleChange: TBillFieldChange;
}

export const FieldError = ({ message }: { message?: string }) =>
    message ? (
        <Text type="danger" style={{ display: 'block', fontSize: 11 }}>
            {message}
        </Text>
    ) : null;

const FieldWarning = () => (
    <Text type="warning" style={{ display: 'block', fontSize: 11 }}>
        Empty value keeps this bill in Needs Review
    </Text>
);

// Uppercase field label. The "AI-extracted" marker lives on the section header (BillSectionHeader), not per field;
// editable inputs get a little extra space below the label.
const FieldLabel = ({ label, spaced }: { label: string; spaced?: boolean }) => (
    <Text style={{ ...billLabelStyle, display: 'block', marginBottom: spaced ? 6 : 0 }}>{label}</Text>
);

const readOnlyValue = (value: string | number | null, type: 'text' | 'number' | 'date') => {
    if (value === null || value === undefined || value === '') return EMPTY_VALUE;
    if (type === 'date') return defaultDateFormat(new Date(value), EMPTY_VALUE, PERIOD_READ_ONLY_FORMAT);
    if (type === 'number') return Number(value).toLocaleString();
    return String(value);
};

interface Props extends IBillSectionProps {
    label: string;
    field: TUpdatableBillField;
    type?: 'text' | 'number' | 'date';
    // Overrides the error lookup (e.g. both period dates share the `billingPeriod` error).
    invalid?: boolean;
    // Stored value that means "no value" (e.g. rate class "NONE"): shown as empty, and written back when the input is cleared.
    emptyValue?: string;
}

// A half-width bill field: an input when the field is editable for this bill, otherwise the read-only value
// (same look as BillReadOnlyField). Editable fields are exactly the ones the source file left empty (or all of them when
// the bill has no matching file row), i.e. the values the AI extracted from the PDF.
export const BillFormField = ({
    label,
    field,
    type = 'text',
    form,
    editableFields,
    errors,
    missingFields,
    disabled,
    handleChange,
    invalid,
    emptyValue,
}: Props) => {
    const storedValue = form[field] as string | number | null;
    const value = emptyValue !== undefined && storedValue === emptyValue ? null : storedValue;
    const error = (errors as Partial<Record<string, string>>)[field];
    const missing = missingFields.has(field);
    const status = error || invalid ? 'error' : missing ? 'warning' : '';
    const aiExtracted = editableFields.has(field);

    if (!aiExtracted || disabled) {
        return (
            <Col span={12}>
                <FieldLabel label={label} />
                <Text style={billValueStyle}>{readOnlyValue(value, type)}</Text>
                {missing && <FieldWarning />}
            </Col>
        );
    }

    return (
        <Col span={12}>
            <FieldLabel label={label} spaced />
            {type === 'number' ? (
                <InputNumber
                    style={{ width: '100%' }}
                    status={status}
                    min={0}
                    max={MAX_BILLING_VALUE}
                    step={0.1}
                    value={value as number | null}
                    onChange={v => handleChange(field, v as never)}
                />
            ) : type === 'date' ? (
                <DatePicker
                    style={{ width: '100%' }}
                    status={status}
                    format={PERIOD_DISPLAY_FORMAT}
                    value={value ? dayjs(value as string) : null}
                    onChange={d => handleChange(field, (d ? d.format(PERIOD_VALUE_FORMAT) : null) as never)}
                />
            ) : (
                <Input
                    status={status}
                    value={(value as string) || ''}
                    onChange={e => handleChange(field, (e.target.value === '' && emptyValue !== undefined ? emptyValue : e.target.value) as never)}
                />
            )}
            {error ? <FieldError message={error} /> : missing && <FieldWarning />}
        </Col>
    );
};
